import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Card } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { upgradeApi } from '../../services/api';
import './Export.css';

const ExportData = () => {
  const { currentUser } = useAuth();
  const [exportType, setExportType] = useState('all'); // all, clicks, conversions
  const [dateRange, setDateRange] = useState('month'); // week, month, year, custom
  const [customRange, setCustomRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [format, setFormat] = useState('csv'); // csv, excel, json
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  
  // Check if user has Pro subscription
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        setCheckingSubscription(true);
        const response = await upgradeApi.getSubscriptionStatus();
        if (response.data.status === 'success') {
          setSubscription(response.data.data.subscription);
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
      } finally {
        setCheckingSubscription(false);
      }
    };
    
    if (currentUser) {
      checkSubscription();
    } else {
      setCheckingSubscription(false);
    }
  }, [currentUser]);
  
  // Check if user has Pro access
  const hasProAccess = subscription?.status === 'active';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate custom date range if selected
    if (dateRange === 'custom') {
      if (!customRange.startDate || !customRange.endDate) {
        toast.error('Please select both start and end dates');
        return;
      }
      
      const startDate = new Date(customRange.startDate);
      const endDate = new Date(customRange.endDate);
      
      if (startDate > endDate) {
        toast.error('Start date must be before end date');
        return;
      }
    }
    
    setLoading(true);
    
    try {
      // In a real application, this would be an API call
      // const res = await axios.get('/api/export', {
      //   params: {
      //     type: exportType,
      //     dateRange,
      //     ...dateRange === 'custom' && customRange,
      //     format
      //   },
      //   responseType: 'blob'
      // });
      
      // const url = window.URL.createObjectURL(new Blob([res.data]));
      // const link = document.createElement('a');
      // link.href = url;
      // link.setAttribute('download', `affiliate-data-export.${format}`);
      // document.body.appendChild(link);
      // link.click();
      // link.remove();
      
      // Mock export for demo
      setTimeout(() => {
        toast.success(`Export of ${exportType} data completed successfully!`);
        setLoading(false);
      }, 2000);
      
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export data. Please try again.');
      setLoading(false);
    }
  };

  // Show loading indicator while checking subscription
  if (checkingSubscription) {
    return (
      <div className="export-page">
        <div className="page-header">
          <h1>Export Data</h1>
        </div>
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Checking subscription status...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="export-page">
      <div className="page-header">
        <h1>Export Data</h1>
      </div>
      
      {!hasProAccess ? (
        <Card className="shadow-sm mb-4">
          <Card.Body className="text-center py-5">
            <h3 className="mb-3">Pro Subscription Required</h3>
            <p className="mb-4">
              Data export is a Pro feature. Upgrade your subscription to export your tracking data for analysis in external tools or for record-keeping.
            </p>
            <div className="features-list mb-4">
              <ul className="text-start mx-auto" style={{ maxWidth: '400px' }}>
                <li>Export clicks and conversions data</li>
                <li>Multiple format options (CSV, Excel, JSON)</li>
                <li>Custom date range selection</li>
                <li>Full historical data access</li>
              </ul>
            </div>
            <Button 
              as={Link} 
              to="/dashboard/subscription" 
              variant="primary" 
              size="lg"
              className="px-4"
            >
              Upgrade to Pro
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div className="export-container">
          <div className="export-description">
            <p>
              Export your tracking data for analysis in external tools or for record-keeping.
              Choose the data type, date range, and format that suits your needs.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="export-form">
          <div className="form-section">
            <h3>What would you like to export?</h3>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="exportType"
                  value="all"
                  checked={exportType === 'all'}
                  onChange={(e) => setExportType(e.target.value)}
                />
                <span className="radio-text">All Data</span>
                <span className="radio-description">Includes clicks, conversions, and link data</span>
              </label>
              
              <label className="radio-label">
                <input
                  type="radio"
                  name="exportType"
                  value="clicks"
                  checked={exportType === 'clicks'}
                  onChange={(e) => setExportType(e.target.value)}
                />
                <span className="radio-text">Clicks Only</span>
                <span className="radio-description">Just click data with timestamps and referrers</span>
              </label>
              
              <label className="radio-label">
                <input
                  type="radio"
                  name="exportType"
                  value="conversions"
                  checked={exportType === 'conversions'}
                  onChange={(e) => setExportType(e.target.value)}
                />
                <span className="radio-text">Conversions Only</span>
                <span className="radio-description">Conversion data with revenue and status</span>
              </label>
            </div>
          </div>
          
          <div className="form-section">
            <h3>Select Date Range</h3>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="dateRange"
                  value="week"
                  checked={dateRange === 'week'}
                  onChange={(e) => setDateRange(e.target.value)}
                />
                <span className="radio-text">Last 7 Days</span>
              </label>
              
              <label className="radio-label">
                <input
                  type="radio"
                  name="dateRange"
                  value="month"
                  checked={dateRange === 'month'}
                  onChange={(e) => setDateRange(e.target.value)}
                />
                <span className="radio-text">Last 30 Days</span>
              </label>
              
              <label className="radio-label">
                <input
                  type="radio"
                  name="dateRange"
                  value="year"
                  checked={dateRange === 'year'}
                  onChange={(e) => setDateRange(e.target.value)}
                />
                <span className="radio-text">Last 12 Months</span>
              </label>
              
              <label className="radio-label">
                <input
                  type="radio"
                  name="dateRange"
                  value="custom"
                  checked={dateRange === 'custom'}
                  onChange={(e) => setDateRange(e.target.value)}
                />
                <span className="radio-text">Custom Range</span>
              </label>
            </div>
            
            {dateRange === 'custom' && (
              <div className="custom-date-range">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={customRange.startDate}
                    onChange={(e) => setCustomRange({...customRange, startDate: e.target.value})}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={customRange.endDate}
                    onChange={(e) => setCustomRange({...customRange, endDate: e.target.value})}
                    className="form-control"
                  />
                </div>
              </div>
            )}
          </div>
          
          <div className="form-section">
            <h3>File Format</h3>
            <div className="radio-group horizontal">
              <label className="radio-label">
                <input
                  type="radio"
                  name="format"
                  value="csv"
                  checked={format === 'csv'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <span className="radio-text">CSV</span>
                <span className="radio-description">Compatible with Excel, Google Sheets</span>
              </label>
              
              <label className="radio-label">
                <input
                  type="radio"
                  name="format"
                  value="excel"
                  checked={format === 'excel'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <span className="radio-text">Excel</span>
                <span className="radio-description">Native Excel format (.xlsx)</span>
              </label>
              
              <label className="radio-label">
                <input
                  type="radio"
                  name="format"
                  value="json"
                  checked={format === 'json'}
                  onChange={(e) => setFormat(e.target.value)}
                />
                <span className="radio-text">JSON</span>
                <span className="radio-description">For developers/API integration</span>
              </label>
            </div>
          </div>
          
          <div className="export-actions">
            <Link to="/conversions" className="btn btn-secondary">
              Cancel
            </Link>
            <button
              type="submit"
              className="btn"
              disabled={loading}
            >
              {loading ? 'Exporting...' : 'Export Data'}
            </button>
          </div>
        </form>
        
        <div className="export-limits" style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#f8f9fa', borderLeft: '3px solid #0d6efd', borderRadius: '4px' }}>
          <h3>Export Limits</h3>
          <p>As a Pro subscriber, you have <strong>unlimited</strong> exports. Export as much data as you need with no monthly restrictions.</p>
        </div>
        </div>
      )}
    </div>
  );
};

export default ExportData;
